/* eslint-disable complexity */
import sha1 from 'js-sha1';
import { IS_ONPREMISE, IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import {
    isPublicRedirectToDownloadControl,
    isPublicRedirectToDownloadExp,
    publicRedirectToDownloadControlLink,
    publicRedirectToDownloadExpLink,
} from 'reactApp/appHelpers/featuresHelpers/features/publicRedirectToDownload';
import { sendSuperAppXray, superAppSubmetrics, superAppTakeItemAndSendXray } from 'reactApp/appHelpers/sendSuperAppAnalytics';
import { getQueryParams, getUtmObject } from 'reactApp/appHelpers/settingsHelpers';
import { setIsFromAttaches } from 'reactApp/modules/attaches/attaches.actions';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getFacesList } from 'reactApp/modules/faces/faces.selectors';
import { cloneRequest } from 'reactApp/modules/modifying/modifying.actions';
import type { ECloneToCloudButtonLocation } from 'reactApp/modules/modifying/modifying.types';
import { getPublicItemType } from 'reactApp/modules/public/public.helpers';
import { getCurrentPublicItem, isOwnPublic } from 'reactApp/modules/public/public.selectors';
import { reDownloadController, ReDownloadEntry } from 'reactApp/modules/requiredAuthorization/reDownloadController';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorageItemById } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store as reduxStore } from 'reactApp/store';
import { renderAuthModalDialog } from 'reactApp/ui/Mobile/AuthModal/AuthModal.helpers';
import { showAuth } from 'reactApp/utils/auth';
import { sendDwh } from 'reactApp/utils/ga';
import { EQueryParams } from 'server/helpers/getRequestParams';

import { getSelectedItems } from './helpers/getSelectedItems';
import { getSelectedItemsOrCurrentFolder } from './helpers/getSelectedItemsOrCurrentFolder';

interface IToolbarClone {
    // source для dwh
    source: string;
    id?: string;
    destination?: string;
    autoClone?: boolean;
    storage?: EStorageType;
    // Не показывать снекбар об успехе клонирования
    noOkSnackbar?: boolean;
    fromReDownload?: boolean;
    // Расположение кнопки "Сохранить в облако"
    button_location?: ECloneToCloudButtonLocation;
}

export const clone = ({
    id,
    source,
    destination,
    autoClone,
    storage,
    noOkSnackbar,
    fromReDownload,
    button_location,
}: IToolbarClone): void => {
    const state = reduxStore.getState();
    const currentStorage = getCurrentStorage(state);
    let items = getSelectedItemsOrCurrentFolder();
    let item = id && getStorageItemById(state, storage || currentStorage, id);
    const currentPublicItem = getCurrentPublicItem(state);
    const utms = getUtmObject();
    const isAnonymous = UserSelectors.isAnonymous(state);
    const isPhone = EnvironmentSelectors.isPhone();

    const isOwn = isOwnPublic(state);
    const faces = getFacesList(state);

    const fromDeeplink = getQueryParams()[EQueryParams.fromDeeplink];
    const isSuperAppWebView = Boolean(IS_WEBVIEW) && fromDeeplink;

    /* tempexp_15344-start */
    if (fromReDownload) {
        const reDownloadParams = reDownloadController.getParams(ReDownloadEntry.Toolbar);

        if (reDownloadParams) {
            items = reDownloadParams.selected;

            if (reDownloadParams.selected?.length > 0) {
                item = undefined;
            }
        } else if (items?.length > 0) {
            item = undefined;
        }
    }
    /* tempexp_15344-end */

    // Отправляем статистику для кнопки "Сохранить в облако"
    if (button_location && currentPublicItem) {
        sendDwh({
            eventCategory: 'public',
            action: 'save-in-cloud-click',
            dwhData: {
                button_location,
                platform: isPhone ? 'touch' : 'web',
                type_public: getPublicItemType(currentPublicItem),
                id_public: isOwn && currentPublicItem?.home ? sha1(currentPublicItem.home) : currentPublicItem?.id,
                webview: IS_WEBVIEW,
                ...utms,
            },
        });

        /**
         * @expFlag public-redirect-to-download
         * @ticket CLOUDWEB-20088
         * @start
         */
        if (isPublicRedirectToDownloadExp) {
            const deepLink = `?mt_deeplink=${window.location.href}`;
            const link = publicRedirectToDownloadExpLink;

            setTimeout(() => {
                window.location.href = link + deepLink;
            }, 100);

            return;
        }
        /**
         * @expFlag public-redirect-to-download
         * @ticket CLOUDWEB-20088
         * @end
         */
    }

    if (isAnonymous && isPhone) {
        renderAuthModalDialog({
            downloadHref: isPublicRedirectToDownloadControl ? publicRedirectToDownloadControlLink : undefined,
        });
        return;
    }

    if (storage === EStorageType.viewerAttaches || storage === EStorageType.attaches) {
        reduxStore.dispatch(setIsFromAttaches(true));
    }
    if (IS_ONPREMISE && isAnonymous) {
        showAuth({});
        return;
    }

    reduxStore.dispatch(
        cloneRequest({
            items: item ? [item] : items,
            source,
            destination,
            autoClone,
            noOkSnackbar,
        })
    );

    if (isSuperAppWebView) {
        const selected = getSelectedItems(state);
        if (selected.length > 0 && currentPublicItem?.isFolder) {
            sendSuperAppXray(superAppSubmetrics.saveInCloud, {
                id_public: isOwn && currentPublicItem?.home ? sha1(currentPublicItem.home) : currentPublicItem?.id,
                id_folder: currentPublicItem?.id,
                type_public: 'folder',
                count_objects: selected.length,
                owner: isOwn,
                have_faces: Boolean(faces?.length),
                count_faces: faces?.length,
            });
        } else {
            if (!currentPublicItem) {
                return;
            }
            superAppTakeItemAndSendXray(superAppSubmetrics.saveInCloud, currentPublicItem, isOwn, faces);
        }
    }
};
